import { FC, memo } from 'react';
import AnchorLink from 'react-anchor-link-smooth-scroll';

import TextLayout from 'features/layout/templates/TextLayout';

import { MetaTags } from 'src/common/Seo';
import { useUpToLaptop } from 'hooks/useMediaQuery';

import {
  BrandBlueSpan,
  PageHeading,
  PageSubheading,
  TextList,
  TextListItem,
  TextParagraph,
  TextSection,
} from 'features/layout/templates/TextLayout/TextLayout.styled';

import { LinksSection, LinksDescription, LinkWrapper, LinksSectionRow } from './TermsOfUse.styled';

const isProd = process.env.NEXT_PUBLIC_APP_CONTEXT === 'production';

const TermsOfUse: FC = () => {
  const isMobile = useUpToLaptop();
  const getAnchorOffset = () => (isMobile ? 120 : 130);

  return (
    <>
      <MetaTags noDefaults canonicalUrl={isProd} title="Gebruikersvoorwaarden Studenten.nl" />
      <TextLayout>
        <PageHeading>Gebruiksvoorwaarden</PageHeading>
        <TextSection>
          <TextParagraph>
            Welkom bij www.studenten.nl (verder benoemd als de &quot;Website&quot;). We danken je
            hartelijk voor je bezoek. Deze Gebruiksvoorwaarden zijn met ingang van 1 januari 2021
            van toepassing en hebben betrekking op het gebruik van de Website.
          </TextParagraph>
        </TextSection>
        <TextSection>
          <TextParagraph>
            Met het bezoeken van onze Website ga je akkoord met de Gebruiksvoorwaarden. Studenten.nl
            raadt iedereen die gebruik maakt van de Website aan om deze Gebruiksvoorwaarden vooraf
            door te nemen. Studenten.nl kan deze Gebruiksvoorwaarden van tijd tot tijd wijzigen. We
            raden je dan ook aan ze regelmatig te lezen.
          </TextParagraph>
        </TextSection>
        <TextSection>
          <PageSubheading>Bescherming van jouw privacy</PageSubheading>
          <TextParagraph>
            In onze Privacyverklaring kun je lezen hoe we omgaan met jouw persoonlijke gegevens en
            op welke manier we jouw privacy beschermen wanneer je gebruikt maakt van onze Website.
          </TextParagraph>
          <LinksSection>
            <LinksSectionRow>
              <LinksDescription>Artikel 1:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-1">
                  Definities
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 2:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-2">
                  Toepasselijkheid
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 3:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-3">
                  Profiel
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 4:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-4">
                  Verplichtingen van Studenten.nl en de gebruiker
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 5:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-5">
                  Beschikbaarheid Website
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 6:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-6">
                  Intellectuele Eigendomsrechten
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 7:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-7">
                  Aansprakelijkheid
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 8:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-8">
                  Overmacht
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 9:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-9">
                  Beëindiging
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 10:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-10">
                  Rechtskeuze en Jurisdictie
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
            <LinksSectionRow>
              <LinksDescription>Artikel 11:</LinksDescription>
              <LinkWrapper>
                <AnchorLink offset={getAnchorOffset} href="#article-11">
                  Varia
                </AnchorLink>
              </LinkWrapper>
            </LinksSectionRow>
          </LinksSection>
        </TextSection>
        <TextSection id="article-1">
          <PageSubheading>Artikel 1. Definities</PageSubheading>
          <TextParagraph>
            <BrandBlueSpan>Content</BrandBlueSpan>: alle informatie die door Studenten.nl op de
            Website wordt geplaatst, waaronder inbegrepen en niet beperkt tot de lay-out en
            look-and-feel van de Website, de logo&apos;s, merken en specifieke teksten.
          </TextParagraph>
          <TextParagraph>
            <BrandBlueSpan>Gebruiksvoorwaarden</BrandBlueSpan>: deze gebruiksvoorwaarden behoren toe
            aan Studenten.nl.
          </TextParagraph>
          <TextParagraph>
            <BrandBlueSpan>Intellectuele Eigendomsrechten</BrandBlueSpan>: alle rechten van
            intellectuele eigendom en de daarmee verbonden rechten, zoals auteursrecht, merkenrecht,
            octrooirecht, modelrecht, handelsnaamrecht, databankenrecht en naburige rechten, alsmede
            rechten op kennis en eenlijnsprestaties.
          </TextParagraph>
          <TextParagraph>
            <BrandBlueSpan>Website</BrandBlueSpan>: de Website is bereikbaar via www.Studenten.nl.
            Op de Website kun je, gratis of tegen een vergoeding, studiekeuzes, studie testen,
            kortingsacties, stages en banen zoeken en vinden.
          </TextParagraph>
          <TextParagraph>
            <BrandBlueSpan>Profiel:</BrandBlueSpan> een profiel betreft een individueel account dat
            door jou wordt aangemaakt en onderhouden, vanaf het moment dat jij je aanmeldt op de
            Website.
          </TextParagraph>
          <TextParagraph>
            <BrandBlueSpan>Studenten.nl</BrandBlueSpan>: betreft een Besloten Vennootschap.
            Studenten.nl B.V. is gevestigd te Loosdrecht, Nieuw-Loosdrechtsedijk 229, 1231 KV.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-2">
          <PageSubheading>Artikel 2. Toepasselijkheid</PageSubheading>
          <TextParagraph>
            2.1. Deze Gebruiksvoorwaarden hebben betrekking op de manier en toepassing waarop jij de
            Website gebruikt.
          </TextParagraph>
          <TextParagraph>
            2.2. Studenten.nl heeft altijd het recht om deze Gebruiksvoorwaarden te wijzigen of uit
            te breiden. Een recente versie van de Gebruiksvoorwaarden kun je terugvinden op de
            Website of worden tijdens jouw gebruik en bezoek van en aan de Website anderszins onder
            jouw aandacht gebracht. Als je de Website ook na het doorvoeren van aanpassingen of
            wijzigingen in de Gebruiksvoorwaarden blijft bezoeken en gebruiken, accepteer je
            onherroepelijk de doorgevoerde wijzigingen of aanvullingen. Als je niet akkoord gaat met
            de wijzigingen of aanvullingen in de Gebruiksvoorwaarden, dan heb je de optie om de
            Website niet langer te gebruiken of jouw persoonlijke account en/of profiel te
            verwijderen.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-3">
          <PageSubheading>Artikel 3. Profiel</PageSubheading>
          <TextParagraph>
            3.1. Om een juist en goed gebruik van de Website te kunnen garanderen, is het
            noodzakelijk om een persoonlijk Profiel aan te maken zoals dat op de Website staat
            beschreven. Je stemt er namens Studenten.nl mee in dat de profielinformatie, die nodig
            is bij het creëren van een persoonlijk Profiel, compleet en correct is. Tijdens het
            registratieproces creëer je een uniek wachtwoord. Met dit wachtwoord krijg je toegang
            tot jouw persoonlijke Profiel en de Website.
          </TextParagraph>
          <TextParagraph>
            3.2. Je bent en blijft te allen tijde persoonlijk verantwoordelijk voor de juistheid en
            compleetheid van de gegevens die je verstrekt. Dat geldt ook voor het wijzigen van deze
            gegevens als deze niet lang voor of op jou van toepassing zijn. Je erkent en begrijpt
            tevens dat het verstrekken van correcte en volledige gegevens van belang is voor een
            optimaal gebruik van de Website.
          </TextParagraph>
          <TextParagraph id="article-3-3">
            3.3. De persoonlijke gegevens die in verband staan met het registratieproces en daartoe
            door jou worden verstrekt, worden opgeslagen binnen een database en verwerkt volgens de
            richtlijnen van ons Privacy Statement. Deze kun je hier bekijken.
          </TextParagraph>
          <TextParagraph id="article-3-4">
            3.4. Als je een persoonlijk Profiel aanmaakt op de Website, ben en blijf je persoonlijk
            verantwoordelijk voor de geheimhouding van het wachtwoord dat je hebt gekozen en de
            eventuele gebruikersnaam. Zowel het wachtwoord als de gebruikersnaam morgen niet aan
            derden worden doorgegeven. Ook het op een andere manier toegang verstrekken tot jouw
            Profiel aan anderen is niet toegestaan. Je bent zelf aansprakelijk voor het gebruik van
            de Website dat middels jouw gebruikersnaam en wachtwoord tot stand komt. Studenten.nl
            gaat ervan uit alleen jij in persoon je op de Website aanmeldt door gebruik te maken van
            de door jouw gekozen gebruikersnaam en wachtwoord. Als je reden hebt om aan te nemen dat
            derden gebruik maken van jouw gebruikersnaam of wachtwoord, of wanneer je weet dat deze
            gegevens in handen zijn van derden, heb je een meldingsplicht aan Studenten.nl.
            Daarnaast is en blijft het ook jouw plicht om in dergelijke situaties zelf doeltreffende
            maatregelen te treffen. Bijvoorbeeld door het veranderen van het wachtwoord.
          </TextParagraph>
          <TextParagraph>
            3.5. Studenten.nl is op elk moment gerechtigd tot het wijzigen, aanpassen of afsluiten
            van de Website, zonder daarbij jegens jou schadeplichtig te worden of zijn. Als je niet
            akkoord kunt gaan met de doorgevoerde wijzigingen, heb je de optie om jouw persoonlijke
            Profiel te verwijderen en niet langer gebruik te maken van de Website.
          </TextParagraph>
          <TextParagraph>
            3.6. Het is Studenten.nl toegestaan om het inlogproces, jouw wachtwoord en/of jouw
            gebruikersnaam te wijzigen als dit noodzakelijk is om een juiste werking van de Website
            te kunnen blijven garanderen.
          </TextParagraph>
          <TextParagraph>
            3.7. Studenten.nl kan geenszins aansprakelijk worden gehouden voor schade die voortkomt
            uit hetzij een onrechtmatige toegang tot de Website en/of het gebruik van de Website
            door anderen.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-4">
          <PageSubheading>
            Artikel 4. Verplichtingen van Studenten.nl en de gebruiker
          </PageSubheading>
          <TextParagraph>
            4.1. Studenten.nl biedt een online platform waarop je studies kunt vergelijken, studie
            testen kunt kopen en stages, bijbanen, kortingen en informatie kunt vinden. Studenten.nl
            zal zich te allen tijde inspannen om de Website zo veel mogelijk bereikbaar en correct
            te houden.
          </TextParagraph>
          <TextParagraph>
            4.2. Je gaat ermee akkoord dat de Website alleen de functies en eigenschappen bevat die
            aanwezig zijn op het moment dat je de Website op “as is” basis gebruikt. Dit betekent
            onder andere dat Studenten.nl geen garanties kan geven op een constante bereikbaarheid
            en correcte bruikbaarheid van de Website (
            <AnchorLink offset={getAnchorOffset} href="#article-5">
              artikel 5
            </AnchorLink>
            ).
          </TextParagraph>
        </TextSection>
        <TextSection id="article-5">
          <PageSubheading>Artikel 5. Beschikbaarheid Website</PageSubheading>
          <TextParagraph>
            5.1 Studenten.nl heeft het recht om, zonder kennisgeving vooraf en zonder schadeplichtig
            te zijn/worden jegens jou, de Website (tijdelijk) buiten gebruik te stellen en/of het
            gebruik ervan te beperken indien dat volgens Studenten.nl nodig is. Bijvoorbeeld om de
            Website van het noodzakelijke onderhoud te kunnen voorzien.
          </TextParagraph>
          <TextParagraph>
            5.2 Studenten.nl heeft het recht om, zonder kennisgeving vooraf, wijzigingen aan te
            brengen binnen de Website. Hetzij voor procedurele wijzigingen of aanpassingen c.q
            verbeteringen van technische aard.
          </TextParagraph>
          <TextParagraph>
            5.3. Studenten.nl kan niet garanderen dat de Website altijd storingsvrij en/of zonder
            onderbrekingen toegankelijk is. Storingen binnen de Website kunnen voorkomen door
            toedoen van onder andere problemen met de internet- of telefoonverbinding, alsmede door
            een computervirus(sen) of andere fouten en/of gebreken. Ten aanzien van jou is
            Studenten.nl op geen enkele manier aansprakelijk of schadeplichtig te houden voor schade
            die voortkomt uit het (tijdelijk) onbereikbaar zijn of uitvallen van de Website.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-6">
          <PageSubheading>Artikel 6. Intellectuele Eigendomsrechten</PageSubheading>
          <TextParagraph>
            6.1. De Intellectuele Eigendomsrechten van de Website en de Content die via de Website
            openbaar toegankelijk is, met uitzondering van content van derden, zoals Studiekeuze123
            en adverteerders uitgezonderd, behoren toe aan Studenten.nl.
          </TextParagraph>
          <TextParagraph>
            6.2. Aan de voorwaarden die toebehoren aan deze Gebruiksvoorwaarden, verleent
            Studenten.nl een gelimiteerd, persoonlijk, herroepelijk, niet-exclusieve, niet
            sublicentieerbare, niet-overdraagbare recht aan de gebruiker, om gebruik te maken van de
            Website en de daartoe behorende Content en deze te lezen en gebruiken op de manier en in
            het format zoals de Website dat ter beschikking stelt.
          </TextParagraph>
          <TextParagraph>
            6.3. Kennisgevingen of vermeldingen die te maken hebben met de Intellectuele
            Eigendomsrechten, mogen niet verwijderd, gewijzigd, verborgen of onleesbaar worden
            gemaakt.
          </TextParagraph>
          <TextParagraph>
            6.4. De inhoud van deze Gebruiksvoorwaarden is niet bedoeld om enige Intellectuele
            Eigendomsrechten over te dragen aan jou. Je voert geen handelingen uit die inbreuk
            hebben of kunnen maken op de Intellectuele Eigendomsrechten van Studenten.nl.
            Voorbeelden zijn domeinnaam registraties of het registreren van brands of Google Adwords
            die overeenstemming toten of vergelijkbaar zijn met enig object waarvan Studenten.nl de
            Intellectuele Eigendomsrechten in zijn bezit heeft.
          </TextParagraph>
          <TextParagraph>
            6.5. Het is niet toegestaan om belangrijke delen van de Website op te vragen, te
            (her)gebruiken of om meermaals en systematisch niet-substantiële delen van de Website op
            te vragen of (hergebruiken zoals aangegeven in de Databankenwet, zonder uitdrukkelijke
            schriftelijke toestemming van Studenten.nl.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-7">
          <PageSubheading>Artikel 7. Aansprakelijkheid</PageSubheading>
          <TextParagraph>
            7.1. Studenten.nl is op geen enkele manier aansprakelijk voor schade die voortkomt uit
            het gebruik van de Website, evenals het niet aansprakelijk is voor gebruik dat wordt of
            is verkregen uit een onrechtmatige actie of anderszins, anders dan in dit artikel en/of
            in de Gebruiksvoorwaarden is bepaald.
          </TextParagraph>
          <TextParagraph>
            7.2. Als Studenten.nl aansprakelijk is voor schade, uit welke hoofde dan ook, jegens
            jou, dan wordt de hoogte van de daarmee gemoeide schadevergoeding beperkt tot het bedrag
            dat door de verzekeraar van Studenten.nl wordt uitgekeerd. Als schadevergoeding door de
            verzekeraar van Studenten.nl omwille van welke reden dan ook niet wordt vergoed of
            uitgekeerd, wordt de hoogte van de schadevergoeding vastgesteld op basis van het
            gestelde binnen artikel{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-11">
              11.3
            </AnchorLink>
            ,{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-11">
              11.4
            </AnchorLink>{' '}
            en{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-11">
              11.5
            </AnchorLink>
            .
          </TextParagraph>
          <TextParagraph>
            7.3. Studenten.nl is alleen en uitsluitend aansprakelijk te houden voor directe schade
            (zoals hieronder beschreven) die wordt geleden door toedoen van een aan Studenten.nl toe
            te kennen tekortkoming en/of het uitvoeren van een onrechtmatige actie. De totale
            aansprakelijkheid onder de overeenkomst bedraagt nooit meer dan EUR.
          </TextParagraph>

          <TextParagraph id="article-7-4">
            7.4. Onder directe schade verstaat Studenten.nl:
            <TextList>
              <TextListItem>&nbsp;&nbsp;&nbsp;Schade aan materiële zaken;</TextListItem>
              <TextListItem>
                &nbsp;&nbsp;&nbsp;Kosten die (redelijkerwijs) zijn gemaakt om schade te kunnen
                beperken of voorkomen die, als gevolg van de gebeurtenis, aannemelijk zijn;
              </TextListItem>
              <TextListItem>
                &nbsp;&nbsp;&nbsp;Redelijke kosten gemaakt ter vaststelling van de schadeoorzaak, de
                aansprakelijkheid, de directe schade en de wijze van herstel.
              </TextListItem>
            </TextList>
          </TextParagraph>
          <TextParagraph>
            7.5. Elke aansprakelijkheid van Studenten.nl, met uitzondering van directe schade, zoals
            gevolgschade is uitgesloten van aansprakelijkheid. Met gevolgschade wordt in dit geval
            bedoeld: een verkeerde studiekeuze, geschillen met aanbieders op de Website, een afname
            van de goodwill, schade die ontstaat door bedrijfsstagnatie, verliezen, kosten die
            worden/zijn gemaakt ter vaststelling van of voor het voorkomen van gevolgschade,
            vermissing, verwisseling of beschadiging van elektronische data en/of schade die is
            ontstaan door een vertraging in het transport van dataverkeer en alle andere schade dan
            genoemd in artikel
            <AnchorLink offset={getAnchorOffset} href="#article-7-4">
              {' '}
              7.4.
            </AnchorLink>
          </TextParagraph>

          <TextParagraph>
            7.6. Deze beperking tot aansprakelijkheid van Studenten.nl heeft geen betrekking op de
            uitsluiting van aansprakelijkheid van Studenten.nl voor doelbewuste roekeloosheid en/of
            opzet van Studenten.nl (&quot; eigen handelen &quot;) in persoon, en/of haar werknemers
            en/of managers.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-8">
          <PageSubheading>Artikel 8. Overmacht</PageSubheading>
          <TextParagraph>
            8.1. Als er sprake is van overmacht, kan Studenten.nl niet aansprakelijk worden gehouden
            voor een toerekenbare tekortkoming die betrekking heeft op de overeenkomst tussen jou en
            Studenten.nl.
          </TextParagraph>
          <TextParagraph>
            8.2. Overmacht kan onder meer betekenen het ziekteverzuim van werknemers en/of de
            absentie van belangrijke medewerkers voor de levering van de Website, onderbrekingen in
            de elektriciteitslevering, stakingen, rebellie, maatregelen van de Overheid,
            tekortschieten van leveranciers van Studenten.nl, tekortkomingen van door Studenten.nl
            in de hand genomen derde partijen, storingen in de internetverbinding, hardware,
            telecommunicatie, netwerken, brand, natuurrampen zoals bijvoorbeeld overstromingen en
            andere omstandigheden die niet kunnen worden voorzien.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-9">
          <PageSubheading>Artikel 9. Beëindiging</PageSubheading>
          <TextParagraph>
            9.1. Als gebruiker heb je het recht om de Website op elk gewenst moment te gebruiken of
            het gebruik daarvan te beëindigen door jouw persoonlijke Profiel te verwijderen.
          </TextParagraph>
          <TextParagraph>
            9.2. Studenten.nl behoudt zich het recht om jouw persoonlijke Profiel te blokkeren, deze
            af sluiten of verwijderen, evenals Studenten.nl het recht heeft om jou het gebruik van
            de Website te ontzeggen als dit gebruik in strijd is met één van de bepalingen uit deze
            Gebruiksvoorwaarden, waaronder ook en niet beperkt tot het bepaalde in de artikelen{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-3">
              3
            </AnchorLink>
            ,{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-3-3">
              3.3
            </AnchorLink>{' '}
            en{' '}
            <AnchorLink offset={getAnchorOffset} href="#article-3-4">
              3.4
            </AnchorLink>
            .
          </TextParagraph>
        </TextSection>
        <TextSection id="article-10">
          <PageSubheading>Artikel 10. Rechtskeuze en jurisdictie</PageSubheading>
          <TextParagraph>
            10.1. Het Nederlands Recht is van toepassing op deze Gebruiksvoorwaarden en alle
            eventuele geschillen die daaruit voortkomen of waarmee deze in verband staan.
          </TextParagraph>
          <TextParagraph>
            10.2. Alle geschillen die voortkomen uit deze Gebruiksvoorwaarden om daarmee in verband
            kunnen worden gebracht, worden behandeld en voorgelegd aan de rechtbank en de rechter
            binnen het arrondissement waarin Studenten.nl gevestigd is.
          </TextParagraph>
        </TextSection>
        <TextSection id="article-11">
          <PageSubheading>Artikel 11. Varia</PageSubheading>
          <TextParagraph>
            11.1. Als er sprake is van nietigheid, gedeeltelijke of gehele ongeldigheid, of
            vernietigbaarheid van enige bepaling uit deze Gebruiksvoorwaarden, dan behouden de
            overige bepalingen in de Gebruiksvoorwaarden hun kracht en geldigheid. Studenten.nl
            draagt er zorg voor dat eventuele nietigheid, gedeeltelijke of gehele ongeldigheid of
            vernietigbaarheid wordt vervangen door een beding dat wel rechtsgeldig is en waarvan de
            (gerechtelijke) gevolgen, met oog op de inhoud en strekking van deze
            Gebruiksvoorwaarden, zoveel mogelijk in overeenstemming zijn met het ongeldige beding.
          </TextParagraph>
          <TextParagraph>
            11.2. Het is Studenten.nl toegestaan om rechten en plichten die voortkomen uit deze
            Gebruiksvoorwaarden over te dragen aan derde partijen en zal je dit ter kennisgeving
            mededelen. Als je niet akkoord gaat met het overdragen van verplichtingen aan derde
            partijen, ben je altijd gerechtigd om niet langer gebruik te maken van de Website en
            jouw persoonlijke Profiel te verwijderen.
          </TextParagraph>
        </TextSection>
      </TextLayout>
    </>
  );
};

export default memo(TermsOfUse);
